// ============================================================================
// Event Manager - Centralized event listener and timer management
// ============================================================================
// Use this class to manage event listeners and timeouts with automatic cleanup
// to prevent memory leaks.

// ----------------------------------------------------------------------------
// Debug logging
// ----------------------------------------------------------------------------
// Opt-in per-event tracing. When enabled, every create/dispose on an
// EventManager instance logs to the console. Toggle from devtools:
//
//   window.__eventManagerDebug.enable()    // persists across reloads
//   window.__eventManagerDebug.disable()
//   window.__eventManagerDebug.status()
//
// State is persisted in localStorage('em.debug'='1') so a reload keeps it on.
// No runtime cost when disabled: the flag is read once per call via a cheap
// boolean check and the log helpers short-circuit.

const LS_KEY = 'em.debug';
let _debug = false;

try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY) === '1') {
        _debug = true;
    }
} catch (e) { /* private mode / SSR */ }

function setEventManagerLogging(enabled) {
    _debug = !!enabled;
    try {
        if (_debug) localStorage.setItem(LS_KEY, '1');
        else localStorage.removeItem(LS_KEY);
    } catch (e) { /* noop */ }
    return _debug;
}

// ----------------------------------------------------------------------------
// Live registry (leak detection)
// ----------------------------------------------------------------------------
// Every EventManager adds itself here on construction and removes itself on
// dispose(). Always on - it's cheap (one Set op per lifecycle) and genuinely
// useful when you need it. Query via `window.__eventManagerDebug.live()`.
//
// A leaked component shows up as an entry that never disappears: open a
// modal, close it, check the list - if its manager is still there, the
// cleanup path is broken.

const _liveManagers = new Set();

// Stable per-manager IDs so logs can be correlated across actions.
let _nextManagerId = 1;

function formatAge(ms) {
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}

function snapshotManager(mgr) {
    const ageMs = Date.now() - mgr._createdAt;
    return {
        tag: mgr._tag,
        label: mgr._label,
        id: mgr._id,
        ageMs,
        age: formatAge(ageMs),
        listeners: mgr.listeners.length,
        bindings: mgr.bindings.size,
        timeouts: mgr.timeouts.length,
        intervals: mgr.intervals.length,
        cleanups: mgr.cleanups.length
    };
}

if (typeof window !== 'undefined') {
    window.__eventManagerDebug = {
        // Logging controls
        enable: () => setEventManagerLogging(true),
        disable: () => setEventManagerLogging(false),
        status: () => _debug,

        // Registry inspection (works whether logging is on or off)
        live: () => [..._liveManagers]
            .map(snapshotManager)
            .sort((a, b) => b.ageMs - a.ageMs),

        // Managers older than `maxAgeMs` - oldest first. Good for
        // "what's left after I closed everything?"
        leaks: (maxAgeMs = 60_000) => {
            const now = Date.now();
            return [..._liveManagers]
                .filter(m => now - m._createdAt > maxAgeMs)
                .map(snapshotManager)
                .sort((a, b) => b.ageMs - a.ageMs);
        },

        // Grouped counts by label - quick check for "how many instances of
        // component X are alive right now?"
        byLabel: () => {
            const groups = {};
            for (const mgr of _liveManagers) {
                const key = mgr._label || '<anonymous>';
                groups[key] = (groups[key] || 0) + 1;
            }
            return groups;
        },

        liveCount: () => _liveManagers.size
    };
}

function describeTarget(el) {
    if (el == null) return '<null>';
    if (typeof window !== 'undefined' && el === window) return 'window';
    if (typeof document !== 'undefined' && el === document) return 'document';
    const tag = el.tagName ? el.tagName.toLowerCase() : '?';
    const id = el.id ? `#${el.id}` : '';
    const cls = typeof el.className === 'string' && el.className
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
    return `<${tag}${id}${cls}>`;
}

// Pulls the first non-internal frame out of a stack trace. Only called when
// debug logging is on, so the cost of `new Error().stack` is accepted.
// Works for both V8 (`at fn (file.js:1:2)`) and Spidermonkey (`fn@file.js:1:2`).
function getCallerLocation() {
    try {
        const stack = new Error().stack;
        if (!stack) return null;
        const lines = stack.split('\n');
        for (const line of lines) {
            if (line.includes('eventManager.js')) continue;
            const match = line.match(/([^\s()/\\]+\.js):(\d+)/);
            if (match) return `${match[1]}:${match[2]}`;
        }
    } catch (e) { /* noop */ }
    return null;
}

/**
 * EventManager - single canonical handle for component lifetime resources.
 * Tracks DOM listeners, timers, subscriptions, observers, and AbortController
 * signals. One `dispose()` call releases everything.
 *
 * Two flavours of registration:
 *
 * - `on()` / `setTimeout()` / `setInterval()` - stacking. Each call adds a
 *   new tracked resource. Use when multiple listeners for the same event
 *   is legitimate, or for one-shot lifecycle attach.
 *
 * - `bind()` / `bindTimeout()` / `bindInterval()` - keyed and idempotent.
 *   Each `key` holds at most one resource; re-binding tears down the old
 *   one first. Use when a setup path can run multiple times and you want
 *   "the" listener, not an accumulating stack.
 *
 * Key naming convention: `<section>:<role>` in kebab-case.
 *   - Component identity lives in the manager's `label`, so keys should NOT
 *     repeat the component name.
 *   - `<section>` groups related bindings within a component
 *     (e.g. `products`, `period`, `client`).
 *   - `<role>` is what the binding does (e.g. `save`, `toggle`, `search`).
 *   - Examples: `products:save`, `products:toggle-new`, `period:year-input`.
 *
 * @example
 *   const events = new EventManager();
 *
 *   // DOM listeners
 *   events.on(button, 'click', handleClick);
 *   events.on(window, 'resize', handleResize);
 *
 *   // Idempotent (safe to call from a re-entrant attach method)
 *   events.bind('save-btn', saveBtn, 'click', () => this.save());
 *
 *   // Timers
 *   events.setTimeout(() => console.log('delayed'), 1000);
 *
 *   // Keyed debounce (each call resets the timer instead of stacking)
 *   events.bindTimeout('search-debounce', doSearch, 250);
 *
 *   // Subscriptions / observers / any custom teardown
 *   events.onCleanup(state.clients.subscribe('updated', refresh));
 *
 *   // Native APIs that take a signal (fetch, addEventListener options, etc.)
 *   fetch(url, { signal: events.signal });
 *
 *   // Later, release everything:
 *   events.dispose();
 */
export class EventManager {
    /**
     * @param {string} [label] - Optional label shown in debug logs to identify
     *                           which component this manager belongs to
     *                           (e.g. 'AddTaskModal', 'BillingSection').
     */
    constructor(label = null) {
        this.listeners = [];
        this.timeouts = [];
        this.intervals = [];
        this.cleanups = [];
        this.bindings = new Map();
        this._abortController = null;

        this._id = _nextManagerId++;
        this._label = label;
        this._tag = label ? `EM#${this._id}[${label}]` : `EM#${this._id}`;
        this._createdAt = Date.now();
        this._disposed = false;

        _liveManagers.add(this);

        if (_debug) console.debug(`[${this._tag}] created`);
    }

    /** Internal: logging helper, no-op when logging is off. */
    _log(action, detail) {
        if (!_debug) return;
        const where = getCallerLocation();
        const line = where ? `[${this._tag}] ${action}  (${where})` : `[${this._tag}] ${action}`;
        if (detail !== undefined) console.debug(line, detail);
        else console.debug(line);
    }

    /**
     * Add an event listener with automatic tracking
     * @param {EventTarget} element - DOM element or event target
     * @param {string} event - Event type (e.g., 'click', 'change')
     * @param {Function} handler - Event handler function
     * @param {Object|boolean} options - addEventListener options
     * @returns {Function} The handler function (for reference)
     */
    on(element, event, handler, options) {
        if (!element || typeof element.addEventListener !== 'function') {
            console.warn('[EventManager] Invalid element provided');
            return handler;
        }

        element.addEventListener(event, handler, options);
        this.listeners.push({ element, event, handler, options });
        if (_debug) this._log(`on ${event} ${describeTarget(element)}`);
        return handler;
    }

    /**
     * Remove a specific event listener
     * @param {EventTarget} element - DOM element or event target
     * @param {string} event - Event type
     * @param {Function} handler - Event handler function
     */
    off(element, event, handler) {
        if (!element || typeof element.removeEventListener !== 'function') return;

        element.removeEventListener(event, handler);
        this.listeners = this.listeners.filter(
            l => !(l.element === element && l.event === event && l.handler === handler)
        );
        if (_debug) this._log(`off ${event} ${describeTarget(element)}`);
    }

    /**
     * Add a one-time event listener (automatically removed after first trigger)
     * @param {EventTarget} element - DOM element or event target
     * @param {string} event - Event type
     * @param {Function} handler - Event handler function
     * @param {Object|boolean} options - addEventListener options
     */
    once(element, event, handler, options) {
        const wrappedHandler = (e) => {
            this.off(element, event, wrappedHandler);
            handler(e);
        };
        this.on(element, event, wrappedHandler, options);
    }

    /**
     * Attach a keyed event listener. Idempotent: if `key` is already bound
     * (to any resource type), the previous binding is torn down first.
     *
     * Use this when a setup/attach method can run more than once and you
     * want exactly one listener per role, not an accumulating stack.
     *
     * @param {string} key - Unique key identifying this binding
     * @param {EventTarget} element - DOM element or event target
     * @param {string} event - Event type
     * @param {Function} handler - Event handler function
     * @param {Object|boolean} [options] - addEventListener options
     * @returns {Function} The handler function (for reference)
     */
    bind(key, element, event, handler, options) {
        if (!element || typeof element.addEventListener !== 'function') {
            console.warn('[EventManager] Invalid element provided to bind()');
            return handler;
        }
        this.unbind(key);
        element.addEventListener(event, handler, options);
        this.bindings.set(key, () => {
            try {
                element.removeEventListener(event, handler, options);
            } catch (e) {
                // element may have been removed from DOM
            }
        });
        if (_debug) this._log(`bind '${key}' ${event} ${describeTarget(element)}`);
        return handler;
    }

    /**
     * Set a keyed timeout. Re-binding the same key clears the pending timer
     * and schedules a fresh one (useful for debounce-style "latest wins").
     *
     * The key is released automatically when the timer fires.
     *
     * @param {string} key - Unique key identifying this timer
     * @param {Function} fn - Function to execute
     * @param {number} delay - Delay in milliseconds
     * @returns {number} Timeout ID
     */
    bindTimeout(key, fn, delay) {
        this.unbind(key);
        const id = setTimeout(() => {
            this.bindings.delete(key);
            if (_debug) this._log(`bindTimeout fire '${key}'`);
            fn();
        }, delay);
        this.bindings.set(key, () => clearTimeout(id));
        if (_debug) this._log(`bindTimeout '${key}' ${delay}ms`);
        return id;
    }

    /**
     * Set a keyed interval. Re-binding the same key clears the previous
     * interval first.
     *
     * @param {string} key - Unique key identifying this interval
     * @param {Function} fn - Function to execute
     * @param {number} delay - Interval in milliseconds
     * @returns {number} Interval ID
     */
    bindInterval(key, fn, delay) {
        this.unbind(key);
        const id = setInterval(fn, delay);
        this.bindings.set(key, () => clearInterval(id));
        if (_debug) this._log(`bindInterval '${key}' ${delay}ms`);
        return id;
    }

    /**
     * Remove a single keyed binding (listener, timer, or interval) by key.
     * No-op if the key is not bound.
     *
     * @param {string} key - The binding key to release
     */
    unbind(key) {
        const teardown = this.bindings.get(key);
        if (!teardown) return;
        try {
            teardown();
        } catch (e) {
            console.error('[EventManager] unbind teardown failed:', e);
        }
        this.bindings.delete(key);
        if (_debug) this._log(`unbind '${key}'`);
    }

    /**
     * Check whether a key is currently bound.
     *
     * @param {string} key - The binding key to check
     * @returns {boolean}
     */
    hasBinding(key) {
        return this.bindings.has(key);
    }

    /**
     * Set a timeout with automatic tracking
     * @param {Function} fn - Function to execute
     * @param {number} delay - Delay in milliseconds
     * @returns {number} Timeout ID
     */
    setTimeout(fn, delay) {
        const id = setTimeout(() => {
            this.timeouts = this.timeouts.filter(t => t !== id);
            if (_debug) this._log(`setTimeout fire #${id}`);
            fn();
        }, delay);
        this.timeouts.push(id);
        if (_debug) this._log(`setTimeout #${id} ${delay}ms`);
        return id;
    }

    /**
     * Clear a specific timeout
     * @param {number} id - Timeout ID to clear
     */
    clearTimeout(id) {
        clearTimeout(id);
        this.timeouts = this.timeouts.filter(t => t !== id);
        if (_debug) this._log(`clearTimeout #${id}`);
    }

    /**
     * Set an interval with automatic tracking
     * @param {Function} fn - Function to execute
     * @param {number} interval - Interval in milliseconds
     * @returns {number} Interval ID
     */
    setInterval(fn, interval) {
        const id = setInterval(fn, interval);
        this.intervals.push(id);
        if (_debug) this._log(`setInterval #${id} ${interval}ms`);
        return id;
    }

    /**
     * Clear a specific interval
     * @param {number} id - Interval ID to clear
     */
    clearInterval(id) {
        clearInterval(id);
        this.intervals = this.intervals.filter(i => i !== id);
        if (_debug) this._log(`clearInterval #${id}`);
    }

    /**
     * Register an arbitrary cleanup callback that runs on dispose().
     * Use this for subscriptions (`state.x.subscribe()`), observers
     * (`new MutationObserver()`), or any resource whose teardown isn't
     * a DOM listener or timer.
     *
     * @example
     *   const unsub = state.clients.subscribe('updated', handler);
     *   events.onCleanup(unsub);
     *
     *   const observer = new ResizeObserver(cb);
     *   observer.observe(el);
     *   events.onCleanup(() => observer.disconnect());
     *
     * @param {Function} fn - Cleanup function called on dispose()
     */
    onCleanup(fn) {
        if (typeof fn === 'function') {
            this.cleanups.push(fn);
            if (_debug) this._log(`onCleanup #${this.cleanups.length}`);
        }
    }

    /**
     * Get an AbortSignal that is aborted when this manager disposes.
     * Useful for native APIs that accept `{ signal }`:
     *   fetch(url, { signal: events.signal })
     *   element.addEventListener('click', fn, { signal: events.signal })
     *
     * The controller is created lazily on first access.
     *
     * @returns {AbortSignal}
     */
    get signal() {
        if (!this._abortController) {
            this._abortController = new AbortController();
        }
        return this._abortController.signal;
    }

    /**
     * Dispose of all managed listeners, timers, subscriptions and cleanups.
     * Call this in component cleanup/unmount.
     */
    dispose() {
        if (_debug) {
            this._log('dispose', {
                listeners: this.listeners.length,
                bindings: this.bindings.size,
                timeouts: this.timeouts.length,
                intervals: this.intervals.length,
                cleanups: this.cleanups.length
            });
        }

        // Remove all event listeners
        this.listeners.forEach(({ element, event, handler, options }) => {
            try {
                element.removeEventListener(event, handler, options);
            } catch (e) {
                // Element may have been removed from DOM
            }
        });
        this.listeners = [];

        // Clear all timeouts
        this.timeouts.forEach(id => clearTimeout(id));
        this.timeouts = [];

        // Clear all intervals
        this.intervals.forEach(id => clearInterval(id));
        this.intervals = [];

        // Tear down keyed bindings (listeners + keyed timers)
        for (const teardown of this.bindings.values()) {
            try {
                teardown();
            } catch (e) {
                console.error('[EventManager] binding teardown failed:', e);
            }
        }
        this.bindings.clear();

        // Run all cleanup callbacks (subscriptions, observers, etc).
        // Run in reverse registration order for LIFO teardown semantics.
        for (let i = this.cleanups.length - 1; i >= 0; i--) {
            try {
                this.cleanups[i]();
            } catch (err) {
                console.error('[EventManager] cleanup callback failed:', err);
            }
        }
        this.cleanups = [];

        // Abort any fetch/listener subscribers that used .signal
        if (this._abortController) {
            try { this._abortController.abort(); } catch (e) { /* noop */ }
            this._abortController = null;
        }

        // Remove from live registry - anything still here is leaking
        this._disposed = true;
        _liveManagers.delete(this);
    }

    /**
     * Get the count of active listeners
     * @returns {number} Number of active listeners
     */
    get listenerCount() {
        return this.listeners.length;
    }

    /**
     * Get the count of active timers (timeouts + intervals)
     * @returns {number} Number of active timers
     */
    get timerCount() {
        return this.timeouts.length + this.intervals.length;
    }

    /**
     * Get the count of registered cleanup callbacks
     * @returns {number} Number of pending onCleanup() callbacks
     */
    get cleanupCount() {
        return this.cleanups.length;
    }

    /**
     * Get the count of active keyed bindings (listeners + timers combined)
     * @returns {number} Number of active bindings
     */
    get bindingCount() {
        return this.bindings.size;
    }
}
